import { useRef, useState } from 'react';
import type { FileError } from 'react-dropzone';
import { useQueryClient } from 'react-query';
import { typedCatchAsync } from '@vy/err';
import { isEmptyArray } from '@vy/utils';
import { queryKeys } from '../../../../../../services/query-key/query-keys';
import { useExtractContractAsync } from '../../../../../../hooks/requests/use-extract-contract-async';
import { useVyNavigate } from '../../../../../../hooks/use-vy-navigate/use-vy-navigate';
import { MAX_CONTRACT_PDF_FILES } from './import-contract-utils';

export type DialogPhase = 'idle' | 'submitting' | 'error';

export function useImportContractDialog(onClose: () => void) {
  const [files, setFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<FileError | null>(null);
  const [phase, setPhase] = useState<DialogPhase>('idle');
  const extractContractMutation = useExtractContractAsync();
  const navigate = useVyNavigate();
  const queryClient = useQueryClient();
  const cancelledRef = useRef(false);

  const resetState = () => {
    setFiles([]);
    setErrorMessage(null);
    setPhase('idle');
    extractContractMutation.reset();
  };

  const handleDeleteFile = (fileToDelete: File) => {
    setFiles((currentFiles) => currentFiles.filter((file) => file !== fileToDelete));
    setErrorMessage(null);
  };

  const handleClose = () => {
    cancelledRef.current = true;
    resetState();
    onClose();
  };

  const handleTryAgain = () => {
    resetState();
  };

  const handleSubmit = async () => {
    if (isEmptyArray(files)) {
      return;
    }

    cancelledRef.current = false;
    setPhase('submitting');

    const extractionResult = await typedCatchAsync(extractContractMutation.mutateAsync({ files }));

    if (!extractionResult.success) {
      console.error('Contract extraction failed', { error: extractionResult.error });
      setPhase('error');
      return;
    }

    if (cancelledRef.current) {
      return;
    }

    await queryClient.invalidateQueries(queryKeys.jobs());

    resetState();
    onClose();
    navigate('/plans');
  };

  const handleFilesSelect = (selectedFiles: File[]) => {
    setErrorMessage(null);
    setFiles((currentFiles) => [...currentFiles, ...selectedFiles].slice(0, MAX_CONTRACT_PDF_FILES));
  };

  const handleFileError = (error: FileError) => {
    setErrorMessage(error);
  };

  return {
    files,
    errorMessage,
    phase,
    handleDeleteFile,
    handleClose,
    handleTryAgain,
    handleSubmit,
    handleFilesSelect,
    handleFileError,
  };
}
